import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Chip,
  Stack,
  Divider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Public as PublicIcon,
  VisibilityOff as PrivateIcon
} from '@mui/icons-material';

interface AlbumSharingDialogProps {
  open: boolean;
  onClose: () => void;
  album: {
    id: string;
    title: string;
    description?: string;
    isPublic?: boolean;
    shareToken?: string;
    shareEnabled?: boolean;
  } | null;
}

const AlbumSharingDialog: React.FC<AlbumSharingDialogProps> = ({
  open,
  onClose,
  album
}) => {
  const [shareUrl, setShareUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const generateShareLink = async () => {
    if (!album) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/${album.id}/generate-share-link`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate share link');
      }

      const data = await response.json();
      setShareUrl(data.shareUrl);
      setSuccess('Share link generated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const disableSharing = async () => {
    if (!album) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/${album.id}/disable-sharing`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to disable sharing');
      }

      setShareUrl('');
      setSuccess('Album sharing disabled successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable sharing');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccess('Link copied to clipboard!');
    } catch (err) {
      setError('Failed to copy link');
    }
  };

  const shareToSocial = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(`Check out "${album?.title}" photo album`);
    
    let shareLink = '';
    
    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${encodedTitle}&body=Check out this photo album: ${shareUrl}`;
        break;
    }

    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!album) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon color="primary" />
            <Typography variant="h6">Share Album</Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            {album.title}
          </Typography>
          <Stack direction="row" spacing={1} mb={2}>
            <Chip
              icon={album.isPublic ? <PublicIcon /> : <PrivateIcon />}
              label={album.isPublic ? 'Public Album' : 'Private Album'}
              color={album.isPublic ? 'success' : 'default'}
              size="small"
            />
          </Stack>
        </Box>

        {!album.isPublic && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This album is private. Only public albums can be shared with a link.
          </Alert>
        )}

        {album.isPublic && (
          <>
            {!shareUrl && !album.shareEnabled && (
              <Box textAlign="center" py={2}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Generate a shareable link for this public album
                </Typography>
                <Button
                  variant="contained"
                  onClick={generateShareLink}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                >
                  {loading ? 'Generating...' : 'Generate Share Link'}
                </Button>
              </Box>
            )}

            {(shareUrl || album.shareEnabled) && (
              <>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Shareable Link
                  </Typography>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      value={shareUrl || `${window.location.origin}/shared/album/${album.shareToken}`}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <Tooltip title="Copy link">
                            <IconButton onClick={copyToClipboard} edge="end">
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        )
                      }}
                      size="small"
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Share on Social Media
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Button
                    startIcon={<FacebookIcon />}
                    onClick={() => shareToSocial('facebook')}
                    size="small"
                    variant="outlined"
                  >
                    Facebook
                  </Button>
                  <Button
                    startIcon={<TwitterIcon />}
                    onClick={() => shareToSocial('twitter')}
                    size="small"
                    variant="outlined"
                  >
                    Twitter
                  </Button>
                  <Button
                    startIcon={<LinkedInIcon />}
                    onClick={() => shareToSocial('linkedin')}
                    size="small"
                    variant="outlined"
                  >
                    LinkedIn
                  </Button>
                  <Button
                    startIcon={<WhatsAppIcon />}
                    onClick={() => shareToSocial('whatsapp')}
                    size="small"
                    variant="outlined"
                  >
                    WhatsApp
                  </Button>
                  <Button
                    startIcon={<EmailIcon />}
                    onClick={() => shareToSocial('email')}
                    size="small"
                    variant="outlined"
                  >
                    Email
                  </Button>
                </Stack>

                <Box mt={2}>
                  <Button
                    color="error"
                    onClick={disableSharing}
                    disabled={loading}
                    size="small"
                  >
                    Disable Sharing
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlbumSharingDialog; 